import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { MemberInvitationResult } from './auth';
import type { MemberInfo, TeamRole } from './teams';
import type { ProjectRoleSummary } from './projectPermissions';
import type { ProjectRepository, ProjectRepositoryBranch } from '@agentteams/runner-request/ui/lib/api';
export type { ProjectRepository, ProjectRepositoryBranch } from '@agentteams/runner-request/ui/lib/api';

export type ProjectVisibility = 'PRIVATE' | 'TEAM';

export type Project = {
  id: string;
  teamId: string;
  name: string;
  description: string | null;
  tags: string[];
  language: string;
  visibility: ProjectVisibility;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type ProjectArchiveFilter = 'active' | 'archived' | 'all';
export type ProjectListScope = 'member' | 'team';

type ProjectListResponse = { data: Project[] };
type ProjectResponse = { data: Project };

export type ProjectMember = {
  id: string;
  projectId: string;
  memberId: string;
  /** 구버전 호환용 미러. 권한 판정과 역할 편집의 기준은 `roles`다. */
  role: TeamRole;
  roles?: ProjectRoleSummary[];
  member: MemberInfo;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

type ProjectMemberResponse = { data: ProjectMember };
type ProjectMemberListResponse = { data: ProjectMember[] };
type ProjectMemberInvitationResponse = { data: MemberInvitationResult };

export type ProjectAuthorOption = {
  memberId: string;
  nickname: string;
};

type ProjectAuthorOptionListResponse = { data: ProjectAuthorOption[] };

export type GithubRepository = {
  id: number;
  name: string;
  fullName: string;
  htmlUrl: string;
  cloneUrl: string;
};

export type GitlabRepository = {
  id: number;
  name: string;
  fullName: string;
  htmlUrl: string;
  cloneUrl: string;
};

export type BitbucketRepository = {
  id: string;
  name: string;
  fullName: string;
  htmlUrl: string;
  cloneUrl: string;
};

export type LinearTeam = {
  id: string;
  name: string;
  key: string;
};

export type ProjectScmProvider = 'GITHUB' | 'GITLAB' | 'BITBUCKET';

export type ProjectScmConnection = {
  provider: ProjectScmProvider;
  isConnected: boolean;
  personalConnected: boolean;
  // 연결 주체 식별 정보(토큰 원문/이메일 등 민감 정보는 포함하지 않음).
  connectedByMemberId: string | null;
  connectedByName: string | null;
  connectedAt: string | null;
};

type ProjectRepositoryResponse = { data: ProjectRepository };
type ProjectRepositoryListResponse = { data: ProjectRepository[] };
type ProjectRepositoryBranchListResponse = { data: ProjectRepositoryBranch[] };
type GithubRepositoryListResponse = { data: GithubRepository[] };
type GitlabRepositoryListResponse = { data: GitlabRepository[] };
type BitbucketRepositoryListResponse = { data: BitbucketRepository[] };
type LinearTeamListResponse = { connected: boolean; teams: LinearTeam[] };
type ProjectScmConnectionListResponse = { data: ProjectScmConnection[] };

export const fetchProjectsByTeam = async (
  token: string,
  teamId: string,
  options?: { archived?: ProjectArchiveFilter; scope?: ProjectListScope },
): Promise<Project[]> => {
  const url = new URL(`${getApiUrl()}/api/teams/${teamId}/projects`);
  if (options?.archived && options.archived !== 'active') {
    url.searchParams.set('archived', options.archived);
  }
  if (options?.scope && options.scope !== 'member') {
    url.searchParams.set('scope', options.scope);
  }

  const response = await fetchWithAuthRetry(url.toString(), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch projects: ${response.status}`);
  }

  return ((await response.json()) as ProjectListResponse).data;
};

export const archiveProject = async (token: string, projectId: string): Promise<Project> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/archive`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to archive project: ${response.status}`);
  }

  return ((await response.json()) as ProjectResponse).data;
};

export const restoreProject = async (token: string, projectId: string): Promise<Project> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/restore`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to restore project: ${response.status}`);
  }

  return ((await response.json()) as ProjectResponse).data;
};

export type CreateProjectResult = { project: Project; seedPlanId: string | null };

export const createProject = async (
  token: string,
  teamId: string,
  input: { name: string; description?: string; visibility?: ProjectVisibility },
): Promise<CreateProjectResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/projects`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create project: ${response.status}`);
  }

  const json = (await response.json()) as { data: Project; seedPlanId?: string | null };
  return { project: json.data, seedPlanId: json.seedPlanId ?? null };
};

export const deleteProject = async (token: string, projectId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete project: ${response.status}`);
  }
};

export const updateProject = async (
  token: string,
  projectId: string,
  input: { name?: string; description?: string; tags?: string[]; language?: string; visibility?: ProjectVisibility },
): Promise<Project> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update project: ${response.status}`);
  }

  return ((await response.json()) as ProjectResponse).data;
};

export const leaveProject = async (token: string, projectId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/leave`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to leave project: ${response.status}`);
  }
};

export const transferProject = async (
  token: string,
  projectId: string,
  input: { targetTeamId: string },
): Promise<Project> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/transfer`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to transfer project: ${response.status}`);
  }

  return ((await response.json()) as ProjectResponse).data;
};

export const fetchProjectById = async (token: string, projectId: string): Promise<Project> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch project: ${response.status}`);
  }

  return ((await response.json()) as ProjectResponse).data;
};

export const fetchProjectMembers = async (token: string, projectId: string): Promise<ProjectMember[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/members`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch project members: ${response.status}`);
  }

  return ((await response.json()) as ProjectMemberListResponse).data;
};

export const fetchProjectAuthorOptions = async (token: string, projectId: string): Promise<ProjectAuthorOption[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/author-options`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch project author options: ${response.status}`);
  }

  return ((await response.json()) as ProjectAuthorOptionListResponse).data;
};

export const addProjectMember = async (
  token: string,
  projectId: string,
  input: { email: string; role: TeamRole; projectRoleId?: string },
): Promise<ProjectMember | MemberInvitationResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/members`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to add project member: ${response.status}`);
  }

  if (response.status === 202) {
    return ((await response.json()) as ProjectMemberInvitationResponse).data;
  }

  return ((await response.json()) as ProjectMemberResponse).data;
};

export const updateProjectMemberRole = async (
  token: string,
  projectId: string,
  memberId: string,
  role: TeamRole,
): Promise<ProjectMember> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/members/${memberId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update project member role: ${response.status}`);
  }

  return ((await response.json()) as ProjectMemberResponse).data;
};

export const removeProjectMember = async (token: string, projectId: string, memberId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/members/${memberId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to remove project member: ${response.status}`);
  }
};

export const fetchProjectRepositories = async (token: string, projectId: string): Promise<ProjectRepository[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/repositories`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch project repositories: ${response.status}`);
  }

  return ((await response.json()) as ProjectRepositoryListResponse).data;
};

export const createProjectRepository = async (
  token: string,
  projectId: string,
  input: {
    name: string;
    provider?: 'GITHUB' | 'GITLAB' | 'BITBUCKET' | 'LINEAR' | 'MANUAL';
    remoteUrl?: string;
    metadata?: Record<string, unknown> | null;
  },
): Promise<ProjectRepository> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/repositories`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create project repository: ${response.status}`);
  }

  return ((await response.json()) as ProjectRepositoryResponse).data;
};

export const deleteProjectRepository = async (
  token: string,
  projectId: string,
  repositoryId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/repositories/${repositoryId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete project repository: ${response.status}`);
  }
};

export const fetchProjectRepositoryBranches = async (
  token: string,
  projectId: string,
  repositoryId: string,
): Promise<ProjectRepositoryBranch[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/repositories/${repositoryId}/branches`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch project repository branches: ${response.status}`);
  }

  return ((await response.json()) as ProjectRepositoryBranchListResponse).data;
};

export const fetchProjectScmConnections = async (token: string, projectId: string): Promise<ProjectScmConnection[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/scm-connections`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch project SCM connections: ${response.status}`);
  }

  return ((await response.json()) as ProjectScmConnectionListResponse).data;
};

export const deleteProjectScmConnection = async (
  token: string,
  projectId: string,
  provider: ProjectScmProvider,
): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/scm-connections/${provider}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete project SCM connection: ${response.status}`);
  }
};

export const buildGithubAuthConnectUrl = (token: string, redirectPath: string, projectId?: string): string => {
  const normalizedPath = redirectPath.startsWith('/') ? redirectPath : '/';
  const url = new URL(`${getApiUrl()}/api/auth/github`);
  url.searchParams.set('redirect', normalizedPath);
  url.searchParams.set('token', token);
  if (projectId) url.searchParams.set('projectId', projectId);
  return url.toString();
};

export const fetchGithubRepositories = async (
  token: string,
  projectId?: string,
): Promise<{ connected: boolean; repositories: GithubRepository[] }> => {
  const url = new URL(`${getApiUrl()}/api/github/repos`);
  if (projectId) url.searchParams.set('projectId', projectId);
  const response = await fetchWithAuthRetry(url.toString(), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (response.status === 404) {
    return { connected: false, repositories: [] };
  }

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch GitHub repositories: ${response.status}`);
  }

  const payload = (await response.json()) as GithubRepositoryListResponse;
  return {
    connected: true,
    repositories: payload.data,
  };
};

export const buildGitlabAuthConnectUrl = (
  token: string,
  redirectPath: string,
  instanceUrl: string,
  projectId?: string,
): string => {
  const normalizedPath = redirectPath.startsWith('/') ? redirectPath : '/';
  const url = new URL(`${getApiUrl()}/api/auth/gitlab`);
  url.searchParams.set('redirect', normalizedPath);
  url.searchParams.set('token', token);
  if (instanceUrl.trim().length > 0) {
    url.searchParams.set('instanceUrl', instanceUrl.trim());
  }
  if (projectId) url.searchParams.set('projectId', projectId);
  return url.toString();
};

export const fetchGitlabRepositories = async (
  token: string,
  projectId?: string,
): Promise<{ connected: boolean; repositories: GitlabRepository[] }> => {
  const url = new URL(`${getApiUrl()}/api/gitlab/repos`);
  if (projectId) url.searchParams.set('projectId', projectId);
  const response = await fetchWithAuthRetry(url.toString(), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (response.status === 404) {
    return { connected: false, repositories: [] };
  }

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch GitLab repositories: ${response.status}`);
  }

  const payload = (await response.json()) as GitlabRepositoryListResponse;
  return {
    connected: true,
    repositories: payload.data,
  };
};

export const buildBitbucketAuthConnectUrl = (token: string, redirectPath: string, projectId?: string): string => {
  const normalizedPath = redirectPath.startsWith('/') ? redirectPath : '/';
  const url = new URL(`${getApiUrl()}/api/auth/bitbucket`);
  url.searchParams.set('redirect', normalizedPath);
  url.searchParams.set('token', token);
  if (projectId) url.searchParams.set('projectId', projectId);
  return url.toString();
};

export const fetchBitbucketRepositories = async (
  token: string,
  projectId?: string,
): Promise<{ connected: boolean; repositories: BitbucketRepository[] }> => {
  const url = new URL(`${getApiUrl()}/api/bitbucket/repos`);
  if (projectId) url.searchParams.set('projectId', projectId);
  const response = await fetchWithAuthRetry(url.toString(), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (response.status === 404) {
    return { connected: false, repositories: [] };
  }

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch Bitbucket repositories: ${response.status}`);
  }

  const payload = (await response.json()) as BitbucketRepositoryListResponse;
  return {
    connected: true,
    repositories: payload.data,
  };
};

export const buildLinearAuthConnectUrl = (token: string, redirectPath: string, projectId: string): string => {
  const normalizedPath = redirectPath.startsWith('/') ? redirectPath : '/';
  const url = new URL(`${getApiUrl()}/api/auth/linear`);
  url.searchParams.set('redirect', normalizedPath);
  url.searchParams.set('token', token);
  url.searchParams.set('projectId', projectId);
  return url.toString();
};

export const buildLinearAppInstallUrl = (token: string, redirectPath: string): string => {
  const normalizedPath = redirectPath.startsWith('/') ? redirectPath : '/';
  const url = new URL(`${getApiUrl()}/api/auth/linear/install`);
  url.searchParams.set('redirect', normalizedPath);
  url.searchParams.set('token', token);
  return url.toString();
};

export const fetchLinearTeams = async (
  token: string,
  projectId: string,
): Promise<{ connected: boolean; teams: LinearTeam[] }> => {
  const url = new URL(`${getApiUrl()}/api/linear/teams`);
  url.searchParams.set('projectId', projectId);
  const response = await fetchWithAuthRetry(url.toString(), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (response.status === 404) {
    return { connected: false, teams: [] };
  }

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch Linear teams: ${response.status}`);
  }

  return (await response.json()) as LinearTeamListResponse;
};

export const fetchLinearAppStatus = async (token: string): Promise<{ installed: boolean }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/linear/app-status`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    return { installed: false };
  }

  return (await response.json()) as { installed: boolean };
};
